import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import type { PreflightReportDetail } from "../../types/preflight.types";
import { ExportReportMenu } from "../ExportReportMenu/ExportReportMenu";
import { ReportFavoriteToggle } from "../ReportFavoriteToggle/ReportFavoriteToggle";
import { ScoringLogicDrawer } from "../ScoringLogicDrawer/ScoringLogicDrawer";
import { RenameReportDialog } from "../RenameReportDialog/RenameReportDialog";
import { ChangeReportMarketDialog } from "../ChangeReportMarketDialog/ChangeReportMarketDialog";
import { DeleteReportDialog } from "../DeleteReportDialog/DeleteReportDialog";

export interface ReportPageActionsProps {
  report: PreflightReportDetail;
  showExport: boolean;
  onDeleted?: () => void;
  className?: string;
}

/**
 * Detail-page actions: one primary CTA (export) and an overflow menu for
 * reference and management tasks (scoring logic, rename, delete).
 */
export function ReportPageActions({
  report,
  showExport,
  onDeleted,
  className,
}: ReportPageActionsProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canRename = permissions.PREFLIGHT_RENAME_REPORT.isOn;
  const canDelete = permissions.PREFLIGHT_DELETE_REPORT.isOn;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [changeMarketOpen, setChangeMarketOpen] = useState(false);

  function openScoringLogic() {
    setMenuOpen(false);
    setScoringOpen(true);
  }

  function openChangeMarket() {
    setMenuOpen(false);
    setChangeMarketOpen(true);
  }

  return (
    <div className={cn("flex shrink-0 items-center gap-2", className)}>
      <ReportFavoriteToggle report={report} placement="header" />

      {showExport ? (
        <ExportReportMenu report={report} />
      ) : null}

      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart="more-horizontal"
          >
            {t("common.more")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={openScoringLogic}>
            <Icon name="info" size="sm" aria-hidden />
            {t("preflight.scoringDrawer.triggerShort")}
          </DropdownMenuItem>
          {canRename ? (
            <RenameReportDialog
              report={report}
              trigger={
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                  <Icon name="pencil" size="sm" aria-hidden />
                  {t("preflight.rename.trigger")}
                </DropdownMenuItem>
              }
            />
          ) : null}
          {canRename ? (
            <DropdownMenuItem onSelect={openChangeMarket}>
              <Icon name="flag" size="sm" aria-hidden />
              {t("preflight.changeMarket.trigger", "Change market")}
            </DropdownMenuItem>
          ) : null}
          {canDelete ? (
            <>
              {canRename ? <DropdownMenuSeparator /> : null}
              <DeleteReportDialog
                report={report}
                onDeleted={onDeleted}
                trigger={
                  <DropdownMenuItem
                    onSelect={(event) => event.preventDefault()}
                    variant="destructive"
                    icon="trash-2"
                  >
                    {t("preflight.delete.trigger")}
                  </DropdownMenuItem>
                }
              />
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ScoringLogicDrawer
        platform={report.platform}
        open={scoringOpen}
        onOpenChange={setScoringOpen}
        trigger={null}
      />

      {canRename && changeMarketOpen ? (
        <ChangeReportMarketDialog
          report={report}
          open={changeMarketOpen}
          onOpenChange={setChangeMarketOpen}
          trigger={null}
        />
      ) : null}
    </div>
  );
}
